import memoize from 'lodash/memoize';
import * as pathToRegexp from 'path-to-regexp';

const toRegexp = memoize(pathToRegexp.pathToRegexp);
const compile = memoize(pathToRegexp.compile);
const match = memoize(pathToRegexp.match);

/** @enum {'address' | 'business'} */
const ADD_FEATURE_TYPE = {
  address: 'address',
  business: 'business',
  road: 'road',
  speedLimit: 'speed-limit',
  incident: 'incident',
  clearance: 'clearance',
};

/** @enum {'location' | 'feedback' | 'additionalInformation' | 'routable-points'} */
enum FEEDBACK_WIZARD_STEP {
  location = 'location',
  feedback = 'feedback',
  additionalInformation = 'additional-information',
  routablePoints = 'routable-points',
}

/**
 * @template T
 * @typedef Route
 * @property {string} path
 * @property {T} __params
 */
/**
 * @typedef {{[K in keyof typeof routes]: (typeof routes)[K]['__params']}} RouteParams
 */

const routes = {
  /** @type {Route<{}>} */
  root: {
    path: '/',
  },
  /** @type {Route<{}>} */
  'results-list': {
    path: '/results',
  },
  /** @type {Route<{}>} */
  details: {
    path: '/details',
  },
  /** @type {Route<{ wizardStep?: FEEDBACK_WIZARD_STEP }>} */
  edit: {
    path: '/edit/:wizardStep?',
  },
  /** @type {Route<{}>} */
  'remove-root': {
    path: '/remove',
  },
  /** @type {Route<{ removeType: string }>} */
  remove: {
    path: '/remove/:removeType',
  },
  /** @type {Route<{}>} */
  'add-root': {
    path: '/add',
  },
  /** @type {Route<{ featureType: ADD_FEATURE_TYPE, wizardStep?: FEEDBACK_WIZARD_STEP }>} */
  add: {
    path: '/add/:featureType/:wizardStep?',
  },
  'routable-points': {
    path: `/:wizardStep?/:parameter?`,
  },
  /** @type {Route<{ feedbackId?: string }>} */
  'thank-you': {
    path: '/thank-you/:feedbackId?',
  },
  /** @type {Route<{}>} */
  error: {
    path: '/error',
  },
};

/**
 * @param {keyof typeof routes} id
 * @returns {string} react-router path
 */
function namedRoute(id, baseUrl?) {
  const route = routes[id];
  if (!route) {
    throw new Error(`Route '${id}' does not exist`);
  }

  if (baseUrl) {
    return baseUrl + route.path;
  }

  return route.path;
}

/**
 *
 * @param {string} route
 * @param {pathToRegexp.ParseOptions & pathToRegexp.RegExpOptions} options
 * @param {string} pathname
 * @returns {boolean}
 */
function pathnameMatches(route, options, pathname) {
  return toRegexp(route, options).test(pathname);
}

/**
 * @param {string} route
 * @param {string} pathname
 * @returns {boolean | object}
 */
function getPathnameMatch<T extends object>(route, pathname) {
  return match<T>(route, { decode: decodeURIComponent })(pathname);
}

/**
 * @template {keyof typeof routes} T
 * @param {T} id
 * @param {RouteParams[T]} params
 */
function compilePath(
  id: string,
  baseUrl?: string | null,
  params?: Record<string, unknown>,
) {
  const route = routes[id];

  if (!route) {
    throw new Error(`Route '${id}' does not exist`);
  }

  const routePath = baseUrl ? baseUrl + route.path : route.path;

  return compile(routePath)(params);
}

function mergeQueries(...queries) {
  const result = new URLSearchParams();
  queries.forEach((q) => {
    const params = new URLSearchParams(q);
    params.forEach((v, k) => {
      const unstringifiedValue = typeof q === 'string' ? v : q[k];
      if (unstringifiedValue === null || unstringifiedValue === undefined) {
        result.delete(k);
      } else {
        result.set(k, v);
      }
    });
  });
  return result.toString();
}

export {
  ADD_FEATURE_TYPE,
  FEEDBACK_WIZARD_STEP,
  namedRoute,
  compilePath,
  mergeQueries,
  pathnameMatches,
  getPathnameMatch,
};
